import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Clock, User2, Users, MapPin, CalendarPlus, Ticket } from "lucide-react";
import { api, fmtTime, googleCalUrl } from "../lib/api";
import { Screen, PageHeader, Empty } from "../components/ui-kit";
import CalendarGrid, { dayKey, itemColor, textOn, DEFAULT_COLORS } from "../components/CalendarGrid";
import MapEmbed from "../components/MapEmbed";

export default function CalendarView() {
  const [params] = useSearchParams();
  const jumpDate = params.get("date");
  const initial = jumpDate ? new Date(`${jumpDate}T12:00:00`) : new Date();
  const [month, setMonth] = useState(() => new Date(initial.getFullYear(), initial.getMonth(), 1));
  const [selected, setSelected] = useState(initial);
  const [selectedItem, setSelectedItem] = useState(null);
  const [classes, setClasses] = useState([]);
  const [events, setEvents] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState(() => localStorage.getItem("cal_filter") || "all");
  const navigate = useNavigate();
  const dayRef = useRef(null);
  const detailRef = useRef(null);
  useEffect(() => { localStorage.setItem("cal_filter", filter); }, [filter]);

  const load = () => {
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/calendar-categories").then((r) => setCats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (jumpDate) setTimeout(() => dayRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 500);
  }, [jumpDate]);

  const colorMap = useMemo(() => {
    const m = { ...DEFAULT_COLORS };
    cats.forEach((c) => { m[c.key] = c.color; });
    return m;
  }, [cats]);
  const catLabel = useMemo(() => {
    const m = {};
    cats.forEach((c) => { m[c.key] = c.label; });
    return m;
  }, [cats]);

  const items = useMemo(() => [
    ...classes.map((c) => ({ ...c, type: "class" })),
    ...events.map((e) => ({ ...e, type: "event" })),
  ], [classes, events]);

  const itemsByDay = useMemo(() => {
    const m = {};
    items.forEach((it) => { if (!it.starts_at) return; const k = dayKey(it.starts_at); (m[k] = m[k] || []).push(it); });
    Object.values(m).forEach((a) => a.sort((x, y) => new Date(x.starts_at) - new Date(y.starts_at)));
    return m;
  }, [items]);

  const selKey = dayKey(selected);
  const dayItems = itemsByDay[selKey] || [];

  const upcoming = useMemo(() => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    return items
      .filter((it) => it.starts_at && new Date(it.starts_at) >= start)
      .filter((it) => filter === "all" || (it.type === "class" ? "classes" : (it.category || "events")) === filter)
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))
      .slice(0, 60);
  }, [items, filter]);

  const jumpToItem = (it) => {
    const d = new Date(it.starts_at);
    setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    setSelected(d);
  };
  const selectItem = (it) => {
    setSelectedItem(it);
    jumpToItem(it);
    if (typeof window !== "undefined" && window.innerWidth < 1024) setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const filterCats = cats.length ? cats : [
    { key: "classes", label: "Classes", color: DEFAULT_COLORS.classes },
    { key: "socials", label: "Socials", color: DEFAULT_COLORS.socials },
    { key: "events", label: "Events", color: DEFAULT_COLORS.events },
  ];

  const calUrl = (it) => googleCalUrl({
    title: it.title,
    startISO: it.starts_at,
    endISO: new Date(new Date(it.starts_at).getTime() + (it.duration_min || (it.type === "class" ? 60 : 120)) * 60000).toISOString(),
    details: it.type === "class" ? `${it.instructor || ""}${it.level ? " · " + it.level : ""} — Darkchata` : `${it.location || ""} — Darkchata`,
  });

  return (
    <Screen>
      <PageHeader overline="Schedule" title="Calendar" />
      <div className="lg:grid lg:grid-cols-5 lg:gap-6 lg:items-start">
        <div className="lg:col-span-2">
          <CalendarGrid month={month} setMonth={setMonth} itemsByDay={itemsByDay} selectedKey={selKey} onSelectDay={(d) => { setSelected(d); setSelectedItem(null); }} colorMap={colorMap} />
          <div className="flex items-center gap-4 mt-4 text-xs text-[#A19DB0] flex-wrap" data-testid="calendar-legend">
            {filterCats.map((c) => (
              <span key={c.key} className="flex items-center gap-1.5" data-testid={`legend-${c.key}`}><span className="w-2.5 h-2.5 rounded-full ring-1 ring-white/20" style={{ background: c.color }} /> {c.label}</span>
            ))}
          </div>

          <div ref={detailRef} className="mt-6 lg:sticky lg:top-6" data-testid="calendar-detail">
            {selectedItem ? (() => {
              const it = selectedItem;
              const col = it.type === "class" ? colorMap.classes : itemColor(it, colorMap);
              return (
                <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden" style={{ borderTop: `3px solid ${col}` }}>
                  {it.image && <img src={it.image} alt="" className="w-full h-40 object-cover" />}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-xl font-semibold">{it.title}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap font-medium shrink-0" style={{ background: col, color: textOn(col) }}>{it.type === "class" ? "Class" : (catLabel[it.category || "events"] || "Event")}</span>
                    </div>
                    <div className="mt-3 space-y-1.5 text-sm text-[#E8E4F0]">
                      <p className="flex items-center gap-2"><CalendarPlus size={14} className="text-[#A19DB0]" />{new Date(it.starts_at).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
                      <p className="flex items-center gap-2"><Clock size={14} className="text-[#A19DB0]" />{fmtTime(it.starts_at)}</p>
                      {it.type === "class"
                        ? (<><p className="flex items-center gap-2"><User2 size={14} className="text-[#A19DB0]" />{it.instructor}{it.level ? ` · ${it.level}` : ""}</p><p className="flex items-center gap-2"><Users size={14} className="text-[#A19DB0]" />{it.slots_left} spots left</p></>)
                        : ((it.location || it.venue) && <p className="flex items-center gap-2"><MapPin size={14} className="text-[#A19DB0]" />{it.location || it.venue}</p>)}
                    </div>
                    {(() => { const q = it.type === "class" ? it.location : (it.location || it.venue); return q ? <div className="mt-3"><MapEmbed query={q} height={160} /></div> : null; })()}
                    {(it.description || it.about) && <p className="text-[#A19DB0] text-sm mt-3 whitespace-pre-line">{it.description || it.about}</p>}
                    <div className="flex gap-2 mt-4">
                      <a href={calUrl(it)} target="_blank" rel="noreferrer" data-testid="detail-add" className="flex-1 rounded-full py-2 text-sm font-semibold bg-white/8 border border-white/15 text-[#E8E4F0] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"><CalendarPlus size={15} /> Add to calendar</a>
                      <button onClick={() => navigate(it.ticket_package_id ? `/packages?pkg=${it.ticket_package_id}` : `/packages?group=${it.type === "class" ? "classes" : (it.category || "events")}`)} data-testid="detail-buy" className="flex-1 rounded-full py-2 text-sm font-semibold bg-[#D4AF37] text-black flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"><Ticket size={15} /> Buy passes</button>
                    </div>
                  </div>
                </div>
              );
            })() : dayItems.length ? (
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <p className="text-[#A19DB0] text-xs mb-3">{selected.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} · {dayItems.length} scheduled</p>
                <div className="space-y-2">
                  {dayItems.map((it) => { const col = it.type === "class" ? colorMap.classes : itemColor(it, colorMap); return (
                    <button key={`${it.type}-${it.id}`} onClick={() => setSelectedItem(it)} data-testid={`day-item-${it.id}`} className="w-full text-left rounded-xl bg-white/5 border border-white/10 p-3 hover:border-[#D4AF37]/40 transition-colors" style={{ borderLeft: `3px solid ${col}` }}>
                      <p className="font-medium text-sm">{it.title}</p>
                      <p className="text-[#A19DB0] text-xs mt-0.5">{fmtTime(it.starts_at)}{it.type === "class" ? ` · ${it.instructor || ""}` : (it.location ? ` · ${it.location}` : "")}</p>
                    </button>
                  ); })}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl bg-white/5 border border-white/10 p-6 text-center text-[#A19DB0] text-sm" data-testid="calendar-detail-empty">Tap a day or an upcoming event to see the full details here.</div>
            )}
          </div>
        </div>

        <div className="lg:col-span-3 mt-8 lg:mt-0" ref={dayRef}>
          <div className="flex gap-2 flex-wrap mb-4" data-testid="calendar-filters">
            <button onClick={() => setFilter("all")} data-testid="filter-all" className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === "all" ? "bg-[#D4AF37] text-black border-transparent" : "bg-white/5 border-white/10 text-[#A19DB0]"}`}>All</button>
            {filterCats.map((c) => (
              <button key={c.key} onClick={() => setFilter(c.key)} data-testid={`filter-${c.key}`} style={filter === c.key ? { background: c.color, color: textOn(c.color) } : undefined}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === c.key ? "border-transparent" : "bg-white/5 border-white/10 text-[#A19DB0]"}`}>{c.label}</button>
            ))}
          </div>

          <h2 className="font-display text-lg font-semibold mb-3">Upcoming</h2>
          {upcoming.length === 0 ? <Empty text="Nothing upcoming in this filter." /> : (
            <div className="space-y-3" data-testid="upcoming-list">
              {upcoming.map((it) => {
                const col = it.type === "class" ? colorMap.classes : itemColor(it, colorMap);
                const isSel = dayKey(new Date(it.starts_at)) === selKey;
                return (
                  <div key={`${it.type}-${it.id}`} data-testid={`upcoming-${it.id}`} className={`rounded-2xl bg-white/5 border p-4 ${isSel ? "border-[#D4AF37]/60" : "border-white/10"}`} style={{ borderLeft: `3px solid ${col}` }}>
                    <button onClick={() => selectItem(it)} data-testid={`upcoming-jump-${it.id}`} className="w-full text-left">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium">{it.title}</p>
                          <p className="text-[#A19DB0] text-xs mt-1 flex flex-wrap gap-x-3 gap-y-1">
                            <span className="flex items-center gap-1"><CalendarPlus size={12} />{new Date(it.starts_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
                            <span className="flex items-center gap-1"><Clock size={12} />{fmtTime(it.starts_at)}</span>
                            {it.type === "class" ? <span className="flex items-center gap-1"><User2 size={12} />{it.instructor}</span> : <span className="flex items-center gap-1"><MapPin size={12} />{it.location}</span>}
                            {it.type === "class" && <span className="flex items-center gap-1"><Users size={12} />{it.slots_left} left</span>}
                          </p>
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap font-medium" style={{ background: col, color: textOn(col) }}>{it.type === "class" ? `${it.credit_cost} pass` : (catLabel[it.category || "events"] || "Event")}</span>
                      </div>
                    </button>
                    <div className="flex gap-2 mt-3">
                      <a href={calUrl(it)} target="_blank" rel="noreferrer" data-testid={`cal-add-${it.id}`} className="flex-1 rounded-full py-2 text-sm font-semibold bg-white/8 border border-white/15 text-[#E8E4F0] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"><CalendarPlus size={15} /> Add to calendar</a>
                      <button onClick={() => navigate(it.ticket_package_id ? `/packages?pkg=${it.ticket_package_id}` : `/packages?group=${it.type === "class" ? "classes" : (it.category || "events")}`)} data-testid={`buy-${it.id}`} className="flex-1 rounded-full py-2 text-sm font-semibold bg-[#D4AF37] text-black flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"><Ticket size={15} /> Buy passes</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Screen>
  );
}
